import { useState, useRef, useEffect, KeyboardEvent, useCallback, useMemo } from "react";
import { X, ArrowUp, Mic, Trash2, Headphones, Check, AlertCircle } from "lucide-react";
import { SpotterArmIcon } from "./SpotterArmIcon";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAIAgent, AIMessage } from "@/hooks/use-ai-agent";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { usePageContext } from "@/hooks/use-page-context";
import { openSupportChat } from "@/components/CrispChat";
import { cn } from "@/lib/utils";

// Simple markdown parser for chat messages.
// Headings use General Sans 600 (font-sans font-semibold) — intentionally NOT Clash Display.
function parseMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listType: "ul" | "ol" | null = null;
  let lineIndex = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === "ul") {
        elements.push(
          <ul key={`list-${lineIndex}`} className="list-disc list-inside space-y-1 my-2">
            {listItems}
          </ul>
        );
      } else {
        elements.push(
          <ol key={`list-${lineIndex}`} className="list-decimal list-inside space-y-1 my-2">
            {listItems}
          </ol>
        );
      }
      listItems = [];
      listType = null;
    }
  };

  const parseInline = (content: string, key: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = content;
    let partIndex = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        parts.push(
          <strong key={`${key}-b-${partIndex}`} className="font-semibold">
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        partIndex++;
        continue;
      }

      const italicMatch = remaining.match(/^\*(.+?)\*/);
      if (italicMatch) {
        parts.push(<em key={`${key}-i-${partIndex}`}>{italicMatch[1]}</em>);
        remaining = remaining.slice(italicMatch[0].length);
        partIndex++;
        continue;
      }

      const codeMatch = remaining.match(/^`(.+?)`/);
      if (codeMatch) {
        parts.push(
          <code
            key={`${key}-c-${partIndex}`}
            className="px-1 py-0.5 bg-muted rounded text-[0.8em] font-mono text-foreground"
          >
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        partIndex++;
        continue;
      }

      const nextSpecial = remaining.search(/[\*`]/);
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        parts.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      }
      partIndex++;
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  for (const line of lines) {
    lineIndex++;
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      elements.push(<div key={`br-${lineIndex}`} className="h-2" />);
      continue;
    }

    // Markdown headings — keep in General Sans, not Clash.
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      const sizeClass = level === 1 ? "text-base" : level === 2 ? "text-[15px]" : "text-sm";
      elements.push(
        <p
          key={`h-${lineIndex}`}
          className={cn("font-sans font-semibold text-foreground mt-3 mb-1.5", sizeClass)}
        >
          {parseInline(content, `h-${lineIndex}`)}
        </p>
      );
      continue;
    }

    const bulletMatch = trimmed.match(/^[-•]\s+(.+)/);
    if (bulletMatch) {
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      listItems.push(
        <li key={`li-${lineIndex}`}>{parseInline(bulletMatch[1], `li-${lineIndex}`)}</li>
      );
      continue;
    }

    const numberedMatch = trimmed.match(/^\d+[.)]\s+(.+)/);
    if (numberedMatch) {
      if (listType !== "ol") {
        flushList();
        listType = "ol";
      }
      listItems.push(
        <li key={`li-${lineIndex}`}>{parseInline(numberedMatch[1], `li-${lineIndex}`)}</li>
      );
      continue;
    }

    flushList();
    elements.push(
      <p key={`p-${lineIndex}`} className="my-1">
        {parseInline(trimmed, `p-${lineIndex}`)}
      </p>
    );
  }

  flushList();
  return elements;
}

function FormattedContent({ content }: { content: string }) {
  const formatted = useMemo(() => parseMarkdown(content), [content]);
  return <div className="space-y-0">{formatted}</div>;
}

// Convert tool name from camelCase to sentence case, lowercased after the first word.
// e.g. "getClientSessions" -> "Get client sessions"
function formatToolName(name: string): string {
  const spaced = name.replace(/([A-Z])/g, " $1").trim();
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Three subtle dots — staggered fade. No "typing..." text.
function ThinkingDots() {
  return (
    <div
      className="flex items-center gap-1.5 py-1"
      role="status"
      aria-label="Spotter is thinking"
    >
      <span
        className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-pulse"
        style={{ animationDelay: "0ms", animationDuration: "1200ms" }}
      />
      <span
        className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-pulse"
        style={{ animationDelay: "200ms", animationDuration: "1200ms" }}
      />
      <span
        className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-pulse"
        style={{ animationDelay: "400ms", animationDuration: "1200ms" }}
      />
    </div>
  );
}

// Tool call badge — muted pill with Geist Mono text.
function ToolCallBadge({
  name,
  status,
}: {
  name: string;
  status: "running" | "succeeded" | "failed";
}) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted",
        "px-2 py-0.5 font-mono text-[11px] text-muted-foreground",
        "animate-in fade-in duration-100"
      )}
    >
      {status === "running" && (
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-pulse" />
      )}
      {status === "succeeded" && (
        <Check className="h-3 w-3 text-success" strokeWidth={2.5} />
      )}
      {status === "failed" && (
        <AlertCircle className="h-3 w-3 text-destructive" strokeWidth={2.5} />
      )}
      <span className="lowercase tracking-tight">{formatToolName(name)}</span>
    </div>
  );
}

function MessageBubble({ message }: { message: AIMessage }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="flex w-full justify-end animate-in fade-in slide-in-from-bottom-1 duration-150">
        <div
          className={cn(
            "max-w-[85%] rounded-2xl px-3.5 py-2",
            "bg-muted text-foreground",
            "font-sans text-[15px] font-medium leading-[1.55]"
          )}
        >
          <div className="whitespace-pre-wrap">{message.content}</div>
        </div>
      </div>
    );
  }

  // Assistant — bare text on background, no bubble. Tiny avatar mark.
  return (
    <div className="flex w-full gap-2.5 animate-in fade-in slide-in-from-bottom-1 duration-150">
      <div className="flex-shrink-0 mt-0.5">
        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
          <SpotterArmIcon className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      <div className="flex-1 min-w-0 font-sans text-[15px] font-normal leading-[1.55] text-foreground">
        {message.isLoading ? (
          <ThinkingDots />
        ) : (
          <FormattedContent content={message.content} />
        )}

        {/* Tool call pills */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.toolCalls.map((tc) => {
              const result = message.toolResults?.find((r) => r.toolCallId === tc.id);
              const status: "running" | "succeeded" | "failed" = !result
                ? "running"
                : result.isError
                  ? "failed"
                  : "succeeded";
              return <ToolCallBadge key={tc.id} name={tc.name} status={status} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Global function to open chat with a message (used by TipsPanel)
let openChatWithMessage: ((message: string) => void) | null = null;

export function askSpotter(message: string) {
  if (openChatWithMessage) {
    openChatWithMessage(message);
  }
}

export function AIAgentChat() {
  const { hasFeature, loading: featureLoading } = useFeatureFlags();
  const { spotterSuggestions, pageTitle, path, section, params } = usePageContext();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");

  const pageContext = { path, pageTitle, section, params };
  const { messages, sendMessage, isLoading, clearHistory } = useAIAgent(pageContext);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isFeatureEnabled = !featureLoading && hasFeature("ai_assistant");

  const handleOpenWithMessage = useCallback(
    (message: string) => {
      setIsOpen(true);
      setTimeout(() => {
        sendMessage(message);
      }, 100);
    },
    [sendMessage]
  );

  useEffect(() => {
    openChatWithMessage = handleOpenWithMessage;
    return () => {
      openChatWithMessage = null;
    };
  }, [handleOpenWithMessage]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    if (!isFeatureEnabled) return;

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFeatureEnabled]);

  if (!isFeatureEnabled) {
    return null;
  }

  const handleSubmit = () => {
    if (input.trim() && !isLoading) {
      sendMessage(input);
      setInput("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      {/* Chat Panel — right-side drawer feel. 200ms ease-out slide. */}
      {isOpen && (
        <div
          className={cn(
            "fixed top-0 right-0 bottom-0 z-50",
            "w-full sm:w-[400px] sm:max-w-[calc(100vw-2rem)]",
            "bg-background border-l border-border",
            "flex flex-col overflow-hidden",
            "animate-in slide-in-from-right duration-200 ease-out"
          )}
          role="dialog"
          aria-label="Spotter"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 h-14 border-b border-border">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center">
                <SpotterArmIcon className="h-4 w-4 text-foreground" />
              </div>
              <h3 className="font-sans font-medium text-[15px] text-foreground">Spotter</h3>
            </div>
            <div className="flex items-center gap-0.5">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={clearHistory}
                  title="Clear chat"
                  aria-label="Clear chat"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea ref={scrollAreaRef} className="flex-1">
            <div className="px-4 py-5">
              {messages.length === 0 ? (
                <div className="flex flex-col items-start pt-6">
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center mb-4">
                    <SpotterArmIcon className="h-5 w-5 text-foreground" />
                  </div>
                  <p className="font-sans text-[15px] leading-[1.55] text-foreground mb-1">
                    Ask me anything about your clients, programs, or business.
                  </p>
                  <p className="font-sans text-[15px] leading-[1.55] text-muted-foreground">
                    I'll help you think it through.
                  </p>
                  {pageTitle && (
                    <p className="mt-3 font-mono text-[11px] text-muted-foreground">
                      on {pageTitle.toLowerCase()}
                    </p>
                  )}

                  {spotterSuggestions.length > 0 && (
                    <div className="mt-5 flex flex-col gap-2 w-full">
                      {spotterSuggestions.slice(0, 3).map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => {
                            setInput(suggestion);
                            inputRef.current?.focus();
                          }}
                          className={cn(
                            "text-left text-[13px] px-3 py-2 rounded-lg",
                            "bg-muted hover:bg-muted/70 text-foreground",
                            "border border-border transition-colors",
                            "font-sans"
                          )}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => {
                      openSupportChat();
                      setIsOpen(false);
                    }}
                    className="mt-6 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
                  >
                    <Headphones className="h-3 w-3" />
                    Talk to a human instead
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  {messages.map((message) => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input area */}
          <div className="border-t border-border p-3">
            <div
              className={cn(
                "flex items-end gap-2 rounded-xl border border-border bg-background",
                "px-2.5 py-2 focus-within:border-ring transition-colors"
              )}
            >
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Spotter anything"
                disabled={isLoading}
                rows={1}
                className={cn(
                  "flex-1 min-h-[28px] max-h-32 resize-none border-0 bg-transparent px-1 py-1",
                  "font-sans text-[15px] leading-[1.55] placeholder:text-muted-foreground",
                  "focus-visible:ring-0 focus-visible:ring-offset-0"
                )}
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0 text-accent hover:text-accent hover:bg-accent/10"
                title="Voice input"
                aria-label="Voice input"
                disabled
              >
                <Mic className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                onClick={handleSubmit}
                disabled={!input.trim() || isLoading}
                className="h-8 w-8 shrink-0 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                aria-label="Send message"
              >
                <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2 px-1 text-[11px] text-muted-foreground">
              <button
                onClick={() => {
                  openSupportChat();
                  setIsOpen(false);
                }}
                className="hover:text-foreground transition-colors flex items-center gap-1 font-sans"
              >
                <Headphones className="h-3 w-3" />
                Contact support
              </button>
              <span className="font-mono">
                <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted">
                  ⌘K
                </kbd>{" "}
                to toggle
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button — gradient volt→iris for attention, matches auth-page logo */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        size="icon"
        data-tour="ai-assistant"
        aria-label={isOpen ? "Close Spotter" : "Open Spotter"}
        className={cn(
          "fixed bottom-6 right-6 z-50 h-14 w-14 rounded-2xl",
          "gradient-primary-bg text-primary-foreground border-0",
          "shadow-lg shadow-primary/30 hover:shadow-primary/40",
          "hover:scale-[1.03] transition-all duration-200",
          isOpen && "opacity-0 pointer-events-none"
        )}
      >
        <SpotterArmIcon className="h-7 w-7 text-white" />
      </Button>
    </>
  );
}
